/*
================================================================================
File: src/components/common/FormGroup.js
Description: Reusable form group components for inputs and selects.
================================================================================
*/
import React, { memo } from "react";
import Select from "react-tailwindcss-select";

export const SelectGroup = memo(({ label, name, value, onChange, options }) => {
  const selectedOption = options.find((option) => option.value === value);
  return (
    <div className="flex items-center justify-between p-2 border-b-2 border-gray-200 dark:border-gray-700 last:border-b-0">
      <label
        className="flex text-xs font-medium text-gray-500 dark:text-gray-300 uppercase flex-shrink-0 mr-4"
        htmlFor={name}
      >
        {label}
      </label>
      <div className="w-full">
        <Select
          name={name}
          value={selectedOption}
          onChange={(selected) => onChange(name, selected.value)}
          options={options}
          classNames={{
            menuButton: ({ isDisabled }) =>
              `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 ${
                isDisabled ? "bg-gray-200 dark:bg-gray-700" : ""
              }`,
            menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200",
            listItem: ({ isSelected }) =>
              `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
                isSelected
                  ? "text-white bg-indigo-500"
                  : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
              }`,
          }}
        />
      </div>
    </div>
  );
});

export const FormGroupSingle = memo(({ name, value, onChange, type }) => (
  <input
    type={type}
    name={name}
    placeholder="Enter Value"
    id={name}
    value={value}
    onChange={onChange}
    className="w-full text-sm font-medium h-9 text-gray-700 text-center bg-white dark:text-gray-200 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
  />
));

export const FormGroup = memo(({ label, name, value, onChange, type }) => (
  <div className="flex items-center justify-between p-2 border-b-2 border-gray-200 dark:border-gray-700 last:border-b-0">
    <label
      className="text-xs font-medium text-gray-500 dark:text-gray-300 uppercase flex-shrink-0 mr-4"
      htmlFor={name}
    >
      {label}
    </label>
    <input
      type={type}
      name={name}
      placeholder="Enter Value"
      id={name}
      value={value}
      onChange={onChange}
      className="w-full text-sm font-medium h-9 text-gray-700 dark:text-gray-200 text-right bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
      style={type === "date" ? { colorScheme: "dark" } : undefined}
    />
  </div>
));

export const SelectAllUserEmails = memo(
  ({ name, value, onChange, userEmails }) => (
    <div className="flex items-center justify-between p-2 border-b-2 border-gray-200 dark:border-gray-700 last:border-b-0">
      <Select
        classNames={{
          menuButton: ({ isDisabled }) =>
            `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 ${
              isDisabled ? "bg-gray-200 dark:bg-gray-700" : ""
            }`,
          menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200",
          listItem: ({ isSelected }) =>
            `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
              isSelected
                ? "text-white bg-indigo-500"
                : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
            }`,
        }}
        name={name}
        value={{ value, label: value }}
        onChange={(selectedOption) => onChange(name, selectedOption.value)}
        options={userEmails}
        className="w-full"
      />
    </div>
  )
);
